import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Terminal,
    RefreshCcw,
    Power,
    Cpu,
    Database,
    X,
    ShieldCheck,
    Activity,
    ChevronDown,
    ChevronUp,
    Search,
    Filter
} from 'lucide-react';

import { API_BASE_URL as API_BASE } from '../../config/apiConfig';


const SystemLogs = () => {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnected, setIsConnected] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAutoScroll, setIsAutoScroll] = useState(true);
    const [historyLimit, setHistoryLimit] = useState(2000);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    
    const consoleRef = useRef(null);
    const historyLimitRef = useRef(historyLimit);

    useEffect(() => {
        historyLimitRef.current = historyLimit;
    }, [historyLimit]);

    const filteredLogs = useMemo(() => {
        if (!searchTerm) return logs;
        const term = searchTerm.toLowerCase();
        return logs.filter(line => line.toLowerCase().includes(term));
    }, [logs, searchTerm]);

    const scrollToBottom = () => {
        if (consoleRef.current) {
            consoleRef.current.scrollTo({
                top: consoleRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    const scrollToTop = () => {
        if (consoleRef.current) {
            consoleRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    const loadMoreLogs = async () => {
        if (isFetchingMore) return;
        setIsFetchingMore(true);
        
        // Save current scroll height to preserve position
        const previousScrollHeight = consoleRef.current?.scrollHeight || 0;
        const newLimit = historyLimit + 2000;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE}/admin/system-logs?lines=${newLimit}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setLogs(data);
                setHistoryLimit(newLimit);
                
                // Use a short timeout to let the DOM update before restoring scroll
                setTimeout(() => {
                    if (consoleRef.current) {
                        const newScrollHeight = consoleRef.current.scrollHeight;
                        consoleRef.current.scrollTop = newScrollHeight - previousScrollHeight;
                    }
                }, 100);
            }
        } catch (error) {
            console.error("Failed to fetch extended telemetry history:", error);
        } finally {
            setIsFetchingMore(false);
        }
    };

    useEffect(() => {
        if (isAutoScroll && !searchTerm) {
            scrollToBottom();
        }
    }, [logs, isAutoScroll, searchTerm]);

    useEffect(() => {
        let socket = null;
        let reconnectTimeout = null;

        const connect = () => {
            let baseUrl = API_BASE.replace('http', 'ws');
            if (baseUrl.includes('localhost')) {
                baseUrl = baseUrl.replace('localhost', '127.0.0.1');
            }
            const wsUrl = `${baseUrl}/admin/ws/logs`;
            
            console.log("📡 Attempting Telemetry Link:", wsUrl);
            socket = new WebSocket(wsUrl);

            socket.onopen = () => {
                console.log("✅ Telemetry Link Established");
                setIsLoading(false);
                setIsConnected(true);
            };

            socket.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    if (message.type === 'backlog') {
                        setLogs(message.data);
                    } else if (message.type === 'log') {
                        setLogs(prev => [...prev, message.data].slice(-historyLimitRef.current));
                    }
                } catch (e) {
                    console.error("Malformed telemetry packet:", event.data);
                }
            };

            socket.onerror = (error) => {
                console.error("❌ Telemetry Link Error:", error);
                setIsConnected(false);
            };

            socket.onclose = (event) => {
                console.log(`🔌 Telemetry Link Severed: Code ${event.code}`);
                setIsConnected(false);
                reconnectTimeout = setTimeout(connect, 3000);
            };
        };

        connect();

        return () => {
            if (socket) socket.close();
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
        };
    }, []);

    const getLevelColor = (line) => {
        if (line.includes('ERROR')) return 'text-rose-400';
        if (line.includes('WARNING')) return 'text-amber-400';
        if (line.includes('INFO')) return 'text-emerald-400';
        return 'text-slate-400';
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        setIsAutoScroll(isAtBottom);
    };

    return (
        <div className="flex flex-col h-[calc(100vh-140px)] animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Professional Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-8">
                <div className="flex flex-col gap-2 pl-4">
                    <div className="flex items-center space-x-3 mb-1">
                        <div className="w-10 h-1 bg-indigo-600 rounded-full"></div>
                        <h1 className="text-5xl font-black text-slate-900 tracking-tighter leading-none flex items-center gap-4">
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-500">System <span className="text-indigo-600">Telemetry</span></span>
                            <div className="h-2 w-2 rounded-full bg-indigo-500 mt-2 animate-pulse" />
                        </h1>
                    </div>
                    <p className="text-slate-500 font-bold text-sm mt-1 max-w-xl leading-relaxed">
                        Real-time Kernel Activity / <span className="text-indigo-600 font-black">Live Backend Stream Monitoring</span>
                    </p>
                </div>

                <div className="flex items-center gap-4">
                    <div className="relative group">
                        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                        <input 
                            type="text"
                            placeholder="Filter telemetry stream..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-12 pr-6 py-3 bg-white rounded-2xl text-[10px] font-black uppercase tracking-widest border border-slate-200 focus:border-indigo-500/30 w-72 transition-all outline-none shadow-sm focus:shadow-xl focus:shadow-indigo-500/5"
                        />
                        {searchTerm && (
                            <button 
                                onClick={() => setSearchTerm('')}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${isConnected ? 'bg-emerald-50 text-emerald-600 border-emerald-100 shadow-xl shadow-emerald-50' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                        {isConnected ? 'Sync Active' : 'Link Offline'}
                    </div>
                    <button 
                        onClick={() => setLogs([])}
                        className="p-3 rounded-xl bg-white text-slate-400 hover:text-rose-500 transition-all border border-slate-200 shadow-sm hover:shadow-md"
                        title="Purge Buffer"
                    >
                        <RefreshCcw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Terminal Container */}
            <div className="flex-1 bg-slate-950 rounded-[2.5rem] border border-slate-800 shadow-2xl flex flex-col overflow-hidden relative group">
                {/* Console Header */}
                <div className="px-10 py-6 bg-slate-900/50 border-b border-slate-800/50 flex items-center justify-between backdrop-blur-xl sticky top-0 z-20">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3">
                            <div className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.4)]" />
                            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        </div>
                        <div className="h-6 w-px bg-slate-800 mx-2" />
                        <div className="flex items-center gap-3">
                            <Terminal className="w-4 h-4 text-indigo-400" />
                            <span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Telemetry Engine v3.0-Active</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-8">
                        <div className="flex items-center gap-3">
                            <Cpu className="w-3.5 h-3.5 text-slate-500" />
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">CPU: <span className="text-emerald-400 tabular-nums">1.4%</span></span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Database className="w-3.5 h-3.5 text-slate-500" />
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Lat: <span className="text-emerald-400 tabular-nums">12ms</span></span>
                        </div>
                    </div>
                </div>

                {/* Console Output */}
                <div 
                    ref={consoleRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto p-10 font-mono no-scrollbar selection:bg-indigo-500/30 scroll-smooth"
                >
                    {/* Load More Trigger */}
                    {!isLoading && logs.length >= 2000 && (
                        <div className="flex justify-center mb-10">
                            <button 
                                onClick={loadMoreLogs}
                                disabled={isFetchingMore}
                                className="px-8 py-3 bg-slate-900/40 hover:bg-indigo-600/20 border border-slate-800 rounded-2xl text-[9px] font-black text-indigo-400 uppercase tracking-[0.3em] transition-all flex items-center gap-3 group disabled:opacity-50"
                            >
                                {isFetchingMore ? (
                                    <>
                                        <RefreshCcw className="w-3.5 h-3.5 animate-spin" />
                                        Accessing Deeper Nodes...
                                    </>
                                ) : (
                                    <>
                                        <ChevronUp className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform" />
                                        Load Older History (2000+ Lines)
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {isLoading && logs.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center gap-6 opacity-40">
                            <div className="relative">
                                <div className="w-16 h-16 border-2 border-indigo-500/20 rounded-full" />
                                <div className="w-16 h-16 border-t-2 border-indigo-500 rounded-full animate-spin absolute inset-0" />
                            </div>
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">Establishing Secure Telemetry...</p>
                        </div>
                    ) : filteredLogs.length > 0 ? (
                        <div className="space-y-1.5">
                            {filteredLogs.map((line, i) => {
                                const parts = line.split(' - ');
                                const timestamp = parts[0];
                                const message = parts.slice(1).join(' - ');
                                return (
                                    <div key={i} className="flex gap-6 group/line hover:bg-white/5 transition-colors rounded px-3 -mx-3 py-0.5">
                                        <span className="text-[10px] text-slate-600 tabular-nums shrink-0 mt-0.5 opacity-60 font-medium">{timestamp}</span>
                                        <span className={`text-[11px] font-medium leading-relaxed break-all ${getLevelColor(line)}`}>
                                            {message}
                                        </span>
                                    </div>
                                );
                            })}
                            <div className="h-10" />
                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center opacity-30 gap-4">
                            <Filter className="w-10 h-10 text-slate-500" />
                            <p className="text-[11px] font-black uppercase tracking-[0.3em]">No matches found in buffer</p>
                        </div>
                    )}
                </div>

                {/* Floating Navigation Controls */}
                <div className="absolute right-8 bottom-24 flex flex-col gap-3 z-30">
                    <button 
                        onClick={scrollToTop}
                        className="p-3 rounded-2xl bg-slate-900/80 text-white hover:bg-indigo-600 border border-slate-800/50 backdrop-blur-md transition-all shadow-2xl active:scale-95 group"
                        title="Scroll to Top"
                    >
                        <ChevronUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
                    </button>
                    <button 
                        onClick={scrollToBottom}
                        className="p-3 rounded-2xl bg-slate-900/80 text-white hover:bg-indigo-600 border border-slate-800/50 backdrop-blur-md transition-all shadow-2xl active:scale-95 group"
                        title="Scroll to Bottom"
                    >
                        <ChevronDown className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
                    </button>
                </div>

                {/* Console Footer Stats */}
                <div className="px-10 py-5 bg-slate-900 border-t border-slate-800/80 backdrop-blur-xl flex items-center justify-between relative z-20">
                    <div className="flex items-center gap-8">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" />
                            <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Security: <span className="text-emerald-400">Locked</span></span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Activity className="w-3.5 h-3.5 text-indigo-500" />
                            <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Health: <span className="text-emerald-400">Stable</span></span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                            {searchTerm ? `${filteredLogs.length} Matches` : `${logs.length} Lines Buffered`}
                        </span>
                        <div className="h-3 w-px bg-slate-800" />
                        <Power className="w-3 h-3 text-emerald-500" />
                        <span className="text-[9px] font-black text-white uppercase tracking-[0.25em]">Kernel Pulse Online</span>
                    </div>
                </div>
            </div>

            <style>{`
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
};

export default SystemLogs;
